// FaviconBadge — overlay a dot on the favicon when there are unread
// messages, and restore the plain favicon when the count is zero.
//
// Render path (design brief § Favicon badge):
//   1. Resolve the current `<link rel="icon">` href. If missing,
//      seed one with an inline SVG so the effect has something to
//      overlay.
//   2. Draw the favicon into a 32×32 canvas.
//   3. Overlay an 8×8 dot in `--chat-unread-dot` at the top-right
//      quadrant.
//   4. Swap the `href` to the canvas data URL.
//   5. When `total` transitions back to zero, swap the href back to
//      the original.
//
// The original href is captured on mount so the cleanup always
// restores the baseline, even if subsequent renders flip through
// several states.
//
// Tauri dock badge: where the runtime exposes a dock badge API
// (macOS), we also update it with the total count so the dock icon
// shows the pill the user expects. On platforms without a badge
// API the call is a silent no-op.

import { useEffect, useRef } from "react";

import { platform } from "~/lib/platform";

const FAVICON_SIZE = 32;
const DOT_DIAMETER = 8;
// 1px halo so the dot reads cleanly against a light favicon.
const DOT_STROKE = 1;

/**
 * Build a plain favicon SVG from the current theme tokens. The
 * component consumes only `--chat-*` custom properties and the
 * `--chat-font-sans` family so the fallback glyph tracks the
 * design-brief palette and typeface. Reads live values via
 * `getComputedStyle` and falls back to neutral values only when
 * those look-ups fail (SSR, JSDOM). Pinned dimensions (32×32) are a
 * hard part of the favicon contract — the browser rasterises at
 * that size regardless — so they stay literal here.
 */
function buildPlainFaviconSvg(): string {
  // 32 and 22 / 18 are required by the favicon contract (rasterised
  // at 32×32 by every browser) and the text baseline — not tokens.
  let accent = "#0AA4FF";
  let onAccent = "#FFFFFF";
  let family = "Inter, ui-sans-serif, system-ui, sans-serif";
  if (typeof document !== "undefined") {
    try {
      const css = getComputedStyle(document.documentElement);
      accent = css.getPropertyValue("--chat-accent").trim() || accent;
      onAccent = css.getPropertyValue("--chat-fg-on-accent").trim() || onAccent;
      family = css.getPropertyValue("--chat-font-sans").trim() || family;
    } catch {
      /* swallow — JSDOM / SSR falls back to the literals above */
    }
  }
  // Single-line SVG so `encodeURIComponent` output stays
  // deterministic across refactors (the shimmer SVG in
  // matrix-sdk-stub uses the same pattern).
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="6" fill="${accent}"/>` +
    `<text x="16" y="22" text-anchor="middle" font-family="${family.replace(/"/g, "'")}" ` +
    `font-size="18" font-weight="600" fill="${onAccent}">N</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export interface FaviconBadgeProps {
  /** Aggregate unread total. Dot appears when > 0. */
  total: number;
  /**
   * Optional override for the base favicon used as the starting
   * canvas. Defaults to the current `<link rel="icon">` href at
   * mount, falling back to a local SVG when none is set.
   */
  baseHref?: string;
}

/**
 * Module-scoped "true original" favicon href. Captured the first
 * time `FaviconBadge` mounts in the tab and re-used by every
 * subsequent mount. Without this, an unmount → remount cycle that
 * happens *while the badge is drawn* (e.g. `Shell` re-renders for an
 * auth transition) would capture the already-badged data URL as the
 * new "original" — the dot would then stick even when `total === 0`.
 */
let trueOriginalHref: string | null = null;

export function FaviconBadge({ total, baseHref }: FaviconBadgeProps) {
  const linkRef = useRef<HTMLLinkElement | null>(null);

  // Capture the `<link>` reference + true-original href exactly once
  // per tab. Re-capturing on every render (or after a remount while
  // the badge is drawn) would let the painted data URL become the
  // "base" for the next paint.
  useEffect(() => {
    if (typeof document === "undefined") return;
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/svg+xml";
      link.href = baseHref ?? buildPlainFaviconSvg();
      document.head.appendChild(link);
    }
    linkRef.current = link;
    // Seed the module-scoped cache only when it's empty — subsequent
    // mounts inherit whatever the first mount captured, so a remount
    // mid-badge never promotes the painted href to "original".
    if (trueOriginalHref === null) {
      trueOriginalHref = baseHref ?? link.href ?? buildPlainFaviconSvg();
    }
    // Reset to base on unmount. The module-scoped cache persists for
    // any future remount.
    return () => {
      const original = trueOriginalHref;
      if (link && original) link.href = original;
    };
    // `baseHref` intentionally omitted from deps: it only seeds on the
    // first mount. A later `baseHref` change would be a semantic
    // reset, which today callers don't use — if they ever need to,
    // add a dedicated effect rather than re-running this one (which
    // would re-seed the trueOriginalHref cache).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw on every `total` change. Loading the base as an image
  // each tick is fine — browsers cache the data / network source
  // and the canvas is tiny.
  //
  // `total === 0` on first mount is the common case (no unreads at
  // boot); the early-return path skips the image load entirely so
  // there's no wasted `<img>` fetch before the user has any
  // unread.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const link = linkRef.current;
    const base = trueOriginalHref;
    if (!link || !base) return;
    if (total <= 0) {
      if (link.href !== base) link.href = base;
      updateDockBadge(0);
      return;
    }
    let cancelled = false;
    const img = new Image();
    // Request CORS headers on cross-origin favicons. Same-origin
    // favicons ignore the attribute and draw cleanly; cross-origin
    // favicons without CORS headers will fail `img.onerror` and
    // fall through to the leave-plain-favicon branch.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = FAVICON_SIZE;
        canvas.height = FAVICON_SIZE;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, FAVICON_SIZE, FAVICON_SIZE);
        // Resolve the dot and halo colours from design tokens so
        // both themes track the accent / surface palette. The CSS
        // custom-property look-up falls back to neutral values
        // only when `getComputedStyle` isn't available.
        const css = getComputedStyle(document.documentElement);
        const dotColor = css.getPropertyValue("--chat-unread-dot").trim() || "#0AA4FF";
        const haloColor = css.getPropertyValue("--chat-surface-app").trim() || "#FFFFFF";
        const cx = FAVICON_SIZE - DOT_DIAMETER / 2 - 2;
        const cy = DOT_DIAMETER / 2 + 2;
        // Halo cut-out so the dot pops against both the favicon and
        // a light OS-themed tab bar (same trick the spaces-rail badge uses).
        ctx.beginPath();
        ctx.fillStyle = haloColor;
        ctx.arc(cx, cy, DOT_DIAMETER / 2 + DOT_STROKE, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = dotColor;
        ctx.arc(cx, cy, DOT_DIAMETER / 2, 0, Math.PI * 2);
        ctx.fill();
        // `toDataURL` throws `SecurityError` when the source image
        // is cross-origin without the right CORS headers — the
        // `img.crossOrigin="anonymous"` request above fails open for
        // compatibility, but the canvas taint is then a runtime
        // error. Wrap the whole draw + readback so a tainted base
        // stays silent and leaves the plain favicon in place rather
        // than surfacing an uncaught exception.
        link.href = canvas.toDataURL("image/png");
      } catch (err) {
        console.warn("FaviconBadge: canvas readback failed, leaving plain favicon", err);
      }
    };
    img.onerror = () => {
      // Origin / CORS issues on the base favicon — leave the plain
      // favicon in place rather than stranding a half-drawn canvas.
      console.warn("FaviconBadge: failed to load base favicon", base);
    };
    img.src = base;
    updateDockBadge(total);
    return () => {
      cancelled = true;
    };
  }, [total]);

  return null;
}

/**
 * Tauri-only: set the OS dock / tray badge count. macOS surfaces
 * this as the pill on the dock icon; other platforms silently
 * ignore the call. On web this is a no-op.
 */
function updateDockBadge(total: number): void {
  if (platform !== "tauri") return;
  void (async () => {
    try {
      // `@tauri-apps/api/app` exposes `setBadgeCount` on platforms
      // that support it (macOS first). The API accepts `undefined`
      // or `0` to clear; we pass `undefined` explicitly to avoid
      // leaving a "0" badge on platforms that render it literally.
      const { setBadgeCount } = (await import("@tauri-apps/api/app")) as {
        setBadgeCount?: (count?: number) => Promise<void>;
      };
      if (typeof setBadgeCount === "function") {
        await setBadgeCount(total > 0 ? total : undefined);
      }
    } catch (err) {
      // Older Tauri versions + non-mac platforms may throw. Swallow —
      // the dock badge is a nice-to-have, not a user-facing guarantee.
      console.warn("FaviconBadge: dock badge update failed", err);
    }
  })();
}
